(function() {
  // Helper to wait until posthog is loaded
  function runWithPostHog(callback) {
    if (window.posthog && typeof window.posthog.onFeatureFlags === 'function') {
      callback();
    } else {
      document.addEventListener('DOMContentLoaded', () => {
        let attempts = 0;
        const interval = setInterval(() => {
          attempts++;
          if (window.posthog && typeof window.posthog.onFeatureFlags === 'function') {
            clearInterval(interval);
            callback();
          } else if (attempts > 50) { // Timeout after 5 seconds
            clearInterval(interval);
            console.warn("PostHog initialization timed out.");
          }
        }, 100);
      });
    }
  }

  function safeGetLocalStorage(key) {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function normalizeVariant(raw) {
    if (raw === true) return 'treatment';
    if (raw === false) return 'control';
    if (raw === 'test') return 'treatment';
    if (raw === 'control' || raw === 'treatment' || raw === 'seed') return raw;
    return null;
  }

  runWithPostHog(() => {
    // 1. Page Context Extraction
    const pageType = window.ShopifyAnalytics?.meta?.page?.pageType || 'page';
    const pageTitle = document.title;
    const urlParams = new URLSearchParams(window.location.search);
    const queryVariant = urlParams.get('v') || '';

    function capturePageview() {
      // Every PDP experiment publishes itself on this one global; the key/storage/
      // property names come from the page so each product's test stays separate.
      const pdpExperiment = window.__mkFullDaySetExperiment;
      const pdpExperimentName = pdpExperiment?.key || 'full-day-set-pdp-variant';
      const pdpStorageKey = pdpExperiment?.storageKey || 'full-day-set-ab-variant';
      const pdpPropPrefix = pdpExperiment?.propPrefix || 'full_day_set';
      const pdpVariant = normalizeVariant(
        pdpExperiment?.variant ||
        document.documentElement.getAttribute('data-ab-variant') ||
        safeGetLocalStorage(pdpStorageKey)
      );
      const pdpSource = pdpExperiment?.source || 'unknown';

      const pdpProps = {};
      pdpProps[pdpPropPrefix + '_experiment_name'] = pdpVariant ? pdpExperimentName : 'none';
      pdpProps[pdpPropPrefix + '_variant'] = pdpVariant || 'none';
      pdpProps[pdpPropPrefix + '_variant_source'] = pdpVariant ? pdpSource : 'none';

      // 2. A/B Testing & Feature Flags Coordination
      const experimentVariant = window.posthog.getFeatureFlag('landing-page-hero-variant');
      let activeVariant = queryVariant || experimentVariant || 'default';

      console.log(`[Moksae PostHog] Active Variant: ${activeVariant} (Query: "${queryVariant}", Experiment Flag: "${experimentVariant}")`);

      // If we have an active A/B test variant and applyVariant function exists on the landing page, trigger it
      const applyFn = window.mlApplyVariant || window.applyVariant;
      if (activeVariant && activeVariant !== 'default' && typeof applyFn === 'function') {
        applyFn(activeVariant);
      }

      window.posthog.capture('$pageview', Object.assign({
        page_type: pageType,
        page_title: pageTitle,
        landing_variant: activeVariant,
        query_variant: queryVariant,
        experiment_variant: experimentVariant || 'none'
      }, pdpProps));
    }

    window.posthog.onFeatureFlags(() => {
      const sharedExperiment = window.__mkFullDaySetExperiment;
      if (sharedExperiment && sharedExperiment.ready && typeof sharedExperiment.ready.then === 'function') {
        sharedExperiment.ready.then(capturePageview).catch(capturePageview);
      } else {
        capturePageview();
      }
    });

    // 3. Exit Intent Detection
    //
    // Fires at most ONCE per page. Both detectors below are cheap to re-trigger
    // -- a desktop user reaching for a browser tab crosses clientY < 0 every
    // time, and a fast scroll back to the top repeats as well -- so ungated
    // they emit the same event dozens of times in a session. That inflates
    // event volume and, worse, makes exit_intent_detected useless as a recording
    // filter: it stops meaning "this person was leaving" and starts meaning
    // "this person moved their mouse". One event per page keeps the signal.
    let exitIntentFired = false;
    function captureExitIntent(deviceType, triggerSource) {
      if (exitIntentFired) return;
      exitIntentFired = true;
      window.posthog.capture('exit_intent_detected', {
        device_type: deviceType,
        trigger_source: triggerSource,
        page_type: pageType,
        url: window.location.href
      });
    }

    // Desktop: Mouse leaving top of the window
    document.addEventListener('mouseleave', (e) => {
      if (e.clientY < 0) {
        captureExitIntent('desktop', 'mouseleave');
      }
    });

    // Mobile: Scroll velocity or exit-intent patterns (rapid scroll up at the top)
    let lastScrollTop = 0;
    let lastScrollTime = Date.now();
    document.addEventListener('scroll', () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const now = Date.now();
      const dt = now - lastScrollTime;
      if (dt > 50) {
        const speed = (lastScrollTop - scrollTop) / dt; // pixels per ms
        // If user scrolls up extremely fast (velocity > 1.5px/ms) near the top of the page (scrollTop < 300)
        if (speed > 1.5 && scrollTop < 300 && lastScrollTop > scrollTop) {
          captureExitIntent('mobile', 'rapid_scroll_up');
        }
        lastScrollTop = scrollTop;
        lastScrollTime = now;
      }
    }, { passive: true });

    // 4. Custom Interaction Tracking (Add to Cart, Checkout, Buy Now clicks)
    document.addEventListener('click', (e) => {
      // 4a. Add to Cart Button Click
      const atcTarget = e.target.closest('#cart-cta, #sticky-btn, .byb__btn, .product-form__submit, #ml-add-to-cart-btn, .ml-sticky-btn');
      if (atcTarget) {
        let bundleName = 'Single Product / Custom';
        let checkedSticks = [];
        let totalPriceVal = '';

        const state = window.mlState || window.state;
        if (state) {
          if (typeof window.bundleLabel === 'function') {
            bundleName = window.bundleLabel();
          } else if (state.checked) {
            const count = state.checked.length;
            if (count === 3) bundleName = 'The Full Day Set';
            else if (count === 2) {
              if (state.checked.includes('morning') && state.checked.includes('midday')) {
                bundleName = 'The Work Day Set';
              } else if (state.checked.includes('midday') && state.checked.includes('night')) {
                bundleName = 'The Slow Down Set';
              } else {
                bundleName = 'The Bookend Set';
              }
            } else if (count === 1) {
              const item = state.checked[0];
              bundleName = { morning: 'Morning Clarity', midday: 'Midday Reset', night: 'Night Unwind' }[item] || item;
            }
            if (state.upsell) {
              bundleName += ' + Havan Cup';
            }
          }

          if (Array.isArray(state.checked)) checkedSticks = state.checked;

          if (typeof window.totalPrice === 'function') {
            totalPriceVal = window.totalPrice();
          } else if (document.getElementById('ml-price-current')) {
            totalPriceVal = document.getElementById('ml-price-current').textContent.replace('₹', '').trim();
          }
        }

        // full-day-set-treatment.js also captures add_to_cart_clicked on the
        // form submit that follows this click; share one timestamp so the add
        // is counted once.
        const now = Date.now();
        if (now - (window.__mkLastAtcCapture || 0) >= 2000) {
          window.__mkLastAtcCapture = now;
          window.posthog.capture('add_to_cart_clicked', {
            page_type: pageType,
            bundle_name: bundleName,
            selected_sticks: checkedSticks,
            total_price: totalPriceVal,
            url: window.location.href
          });
        }
      }

      // 4c. Buy Now / Dynamic Checkout Button Click
      const buyNowTarget = e.target.closest('.shopify-payment-button, .shopify-payment-button__button, .shopify-payment-button button');
      if (buyNowTarget) {
        console.log('[Moksae PostHog] Buy Now Clicked');
        window.posthog.capture('buy_now_clicked', {
          page_type: pageType,
          url: window.location.href
        });
      }
    });

    // 4b. Checkout Button Click — window, capture phase. Razorpay Magic
    // Checkout stops the click in its own window capture listener, so nothing
    // on document (capture or bubble) ever saw it (checkout_clicked: 40/month
    // against 187 Magic checkouts opened). Other window listeners still run.
    window.addEventListener('click', (e) => {
      const checkoutTarget = e.target.closest('#checkout, #CartDrawer-Checkout, button[name="checkout"], .cart__checkout-button');
      if (checkoutTarget) {
        window.posthog.capture('checkout_clicked', {
          page_type: pageType,
          url: window.location.href
        });
      }
    }, true);

    // 5. Razorpay Magic Checkout funnel
    //
    // Magic Checkout orders never reach Shopify's checkout or order status
    // page, so neither the custom pixel nor main-order.liquid records them and
    // PostHog had no purchases at all. Magic reports each checkout step to Meta
    // through the page's window.fbq; mirror those calls into PostHog. fbq is
    // wrapped in a Proxy so every property Meta's library reads or sets
    // (queue, loaded, callMethod, getState...) still reaches the real function.
    const MAGIC_EVENTS = {
      magic_checkout_requested: 'magic_checkout_opened',
      login_success: 'magic_checkout_login_success',
      login_failed: 'magic_checkout_login_failed',
      AddPaymentInfo: 'magic_checkout_payment_info',
      pay_now_clicked: 'magic_checkout_pay_clicked',
      payment_failed: 'magic_checkout_payment_failed',
      // Prepaid orders send both; COD orders may send only Purchase.
      payment_successful: 'order_completed',
      Purchase: 'order_completed'
    };
    let orderCaptured = false;
    function mirrorFbq(args) {
      try {
        const method = args[0];
        const name = args[1];
        if (method !== 'track' && method !== 'trackCustom') return;
        const phEvent = MAGIC_EVENTS[name];
        if (!phEvent) return;
        if (phEvent === 'order_completed') {
          if (orderCaptured) return;
          orderCaptured = true;
        }
        const data = args[2] || {};
        window.posthog.capture(phEvent, {
          source: 'razorpay-magic-checkout',
          value: data.value,
          // Same name the custom pixel uses for Shopify-checkout orders, so the
          // checkout-provider-test revenue metric compares like with like.
          total_price: data.value,
          currency: data.currency,
          content_ids: data.content_ids,
          num_items: data.num_items,
          url: window.location.href
        });
      } catch (err) {}
    }
    function wrapFbq() {
      const fbq = window.fbq;
      if (typeof fbq !== 'function' || fbq.__mkMirrored) return;
      const proxied = new Proxy(fbq, {
        apply(target, thisArg, args) {
          mirrorFbq(args);
          return Reflect.apply(target, thisArg, args);
        },
        get(target, prop, receiver) {
          if (prop === '__mkMirrored') return true;
          return Reflect.get(target, prop, receiver);
        }
      });
      window.fbq = proxied;
      if (window._fbq === fbq) window._fbq = proxied;
    }
    // fbq is defined by an app script that may run after this one, and
    // something could reassign it later; keep re-checking for the first minute.
    wrapFbq();
    let fbqChecks = 0;
    const fbqTimer = setInterval(() => {
      wrapFbq();
      if (++fbqChecks >= 60) clearInterval(fbqTimer);
    }, 1000);

  });
})();
